import { useNavigate, useParams } from 'react-router';
import { ScreenSkeleton } from '../../../../components/atoms/ScreenSkeleton';
import { ObservableLevelsEnum, PartName } from '../../../../data/enums';
import { CompleteButton } from '../../../../components/atoms/Button';
import { useEffect, useMemo, useState } from 'react';
import { ScreenHeading } from '../../../../components/molecules/ScreenHeading';
import { ActionStatusBar } from '../../../../components/molecules/ActionStatusBar';
import PhotoSelector from '../../../../components/atoms/PhotoSelector';
import { RichTextField } from '../../../../components/atoms/RichTextField';
import CheckboxWithLabel from '../../../../components/atoms/Checkbox';
import {
  SingleLineTextField,
  SingleLineValidation,
} from '../../../../components/atoms/SingleLineTextField';
import { Label } from '../../../../components/atoms/Label';
import { ScreenObservationLayout } from '../../../../components/templates/ScreenObservationLayout';
import { ObservableItemIdentifier } from '../../../../api-contracts/other/ObservableItemIdentifier';
import { MdHelpers } from '../../../../helpers/mdHelpers';
import { ErrorBanner } from '../../../../components/atoms/ErrorBanner';
import useInspectionCategoryType from '../../../../hooks/useInspectionCategoryType';
import { inspectionDB } from '../../../../data/database.structure';
import { ActionTypeStore } from '../../../../store/inspection/observation/actionTypeStore';
import { useInspectionCached } from '../../../../hooks/useInspectionCached';
import { useAssetEquipmentPartCached } from '../../../../hooks/useAssetEquipmentPartCached';
import { ObservationDraftScope } from '../../../../components/ObservationDraftScope';
import usePartDimensionForPath from '../../../../hooks/usePartDimensionForPath';
import WearDisplayHelpers from '../../../../helpers/wearDisplayHelpers';

export const DriveTumblerDriveLugScreen = () => {
  const { externalId, sideName, partCategory } = useParams();
  const navigate = useNavigate();

  const [measurementValidationError, setErrorValidation] =
    useState<SingleLineValidation>(null);

  const { inspection } = useInspectionCached(externalId);

  const undercarriageCategory = useInspectionCategoryType({
    inspection,
  });

  const {
    equipmentPart,
    error: errorEquipmentPart,
    numberOfShoes,
  } = useAssetEquipmentPartCached({
    externalId,
    asset: inspection?.asset,
    sideName,
    partCategory,
    partName: PartName.driveLug,
  });
  const [error, setError] = useState<string | null>(null);
  const [rangeError, setRangeError] = useState<string | null>(null);

  // #10205: acceptable range visible before the user types. DISPLAY-ONLY.
  const { partDimension, isLoaded: partDimensionLoaded } =
    usePartDimensionForPath({
      path: ObservableItemIdentifier.Identifiers.Undercarriage.DriveLug
        .WornLength.Measurement,
      equipmentPartExternalId: equipmentPart?.externalId,
    });

  useEffect(() => {
    setError(null);
    async function getEquipment() {
      try {
        if (errorEquipmentPart) {
          setError(errorEquipmentPart);
        }
      } catch (error) {
        console.error('Error fetching equipment', error);
        setError('Error fetching equipment');
      }
    }

    getEquipment();
  }, [errorEquipmentPart, equipmentPart]);

  const { Undercarriage } = ObservableItemIdentifier.Identifiers;
  const listWearIndicators = useMemo(
    () => [
      {
        label: 'Plastic Deformation',
        path: Undercarriage.DriveLug.WearIndicators.PlasticDeformation,
      },
      {
        label: 'Spalling / Delamination / Minor Cracking',
        path: Undercarriage.DriveLug.WearIndicators
          .SpallingDelaminationMinorCracking,
      },
      {
        label: 'Severe Cracking / Broken',
        path: Undercarriage.DriveLug.WearIndicators.SevereCracking,
      },
    ],
    [],
  );

  if (
    !equipmentPart ||
    !numberOfShoes ||
    !externalId ||
    !undercarriageCategory
  ) {
    return <ScreenSkeleton fields={4} />;
  }

  if (error) {
    return <ErrorBanner message={error} />;
  }

  return (
    <ObservationDraftScope inspectionId={externalId}>
      <div className="-mt-4">
        <div className="sticky -top-4 z-10 bg-white pt-4">
          <ScreenHeading
            eyebrow={'Drive tumbler'}
            tag={sideName?.split('-')[0]}
            title={'Drive lug'}
            partNumber={equipmentPart.part.partNumber}
            tooltipKey={'undercarriage-drive-tumbler-drive-lug-screen-header'}
          />

          <div className="-mx-4 mt-2 bg-white">
            <ActionStatusBar
              inspectionId={externalId}
              inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
              dataLevel={ObservableLevelsEnum.equipmentPart}
              entities={[
                {
                  entityId: equipmentPart.id,
                  entityExternalId: equipmentPart.externalId,
                },
              ]}
              paths={[
                Undercarriage.DriveLug.WornLength.Measurement,
                ...listWearIndicators.map((e) => e.path),
              ]}
              pathsLike={[]}
            />
          </div>
        </div>

        <ScreenObservationLayout>
          <Label titleType="fieldTitle" tooltipKey="drive-tumbler-worn-length">
            Measured Length
          </Label>

          <SingleLineTextField
            title="Measurement (mm)"
            path={Undercarriage.DriveLug.WornLength.Measurement}
            dataLevel={ObservableLevelsEnum.equipmentPart}
            entityId={equipmentPart.id}
            entityExternalId={equipmentPart.externalId}
            inspectionId={externalId}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
            type="float"
            tooltipKey={Undercarriage.DriveLug.WornLength.Measurement}
            placeholder="Measurement (mm)"
            validationError={measurementValidationError}
            onChange={async (value) => {
              if (value === '') {
                return;
              }
              const valueFloat = parseFloat(value);
              if (isNaN(valueFloat)) {
                setErrorValidation('InvalidInput');
                setTimeout(() => {
                  setErrorValidation('');
                }, 1500);
                return;
              }
              const observableItem = await inspectionDB.observableItems
                .where('identifier')
                .equals(
                  ObservableItemIdentifier.Identifiers.Undercarriage.DriveLug
                    .WornLength.Measurement,
                )
                .first();
              if (!observableItem) {
                setErrorValidation('InvalidInput');
                setTimeout(() => {
                  setErrorValidation('');
                }, 1500);
                return;
              }
              const actionStatus =
                await new ActionTypeStore().getPartActionStatus({
                  equipmentPartId: equipmentPart.externalId,
                  value: valueFloat,
                  observableItemId: observableItem.id,
                });
              const isValid = ActionTypeStore.validateActionStatus({
                actionStatus: actionStatus,
                value: valueFloat,
              });
              if (
                !isValid &&
                (actionStatus.percentage < 0 || actionStatus.percentage > 250)
              ) {
                // #10205: rejection message includes the acceptable range
                setRangeError(
                  WearDisplayHelpers.formatOutOfRangeError({
                    value: valueFloat,
                    minValue: actionStatus.minValue,
                    maxValue: actionStatus.maxValue,
                  }),
                );
                setErrorValidation('BelowOrEqualZero');
                setTimeout(() => {
                  setErrorValidation('');
                }, 1500);
                return;
              }
              setRangeError(null);
            }}
          />
          {/* #10205: acceptable range visible before the user types */}
          {partDimensionLoaded && (
            <div className="text-sm text-inkMuted">
              {WearDisplayHelpers.formatAcceptableRangeHelperText({
                dimension: partDimension,
              })}
            </div>
          )}
          {rangeError && <p className="text-sm text-red-500">{rangeError}</p>}

          <Label
            titleType="fieldTitle"
            tooltipKey="undercarriage-drive-tumbler-drive-lug-wear-indicators"
          >
            Wear Indicators
          </Label>
          {listWearIndicators.map((e) => (
            <CheckboxWithLabel
              key={e.path}
              inspectionId={externalId}
              inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
              dataLevel={ObservableLevelsEnum.equipmentPart}
              entityId={equipmentPart.id}
              entityExternalId={equipmentPart.externalId}
              {...e}
              infoTooltipKey={`undercarriage-drive-tumbler-drive-lug-wearindicators-${MdHelpers.normaliseTooltipKeyComponent(e.label)}`}
            />
          ))}
          <PhotoSelector
            inspectionId={externalId}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
            dataLevel={ObservableLevelsEnum.equipmentPart}
            entityId={equipmentPart.id}
            entityExternalId={equipmentPart.externalId}
            title="Photos"
            path={Undercarriage.DriveLug.Photo}
          />
          <RichTextField
            label="Comments"
            className="mt-4"
            path={Undercarriage.DriveLug.Comments}
            inspectionId={externalId}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
            dataLevel={ObservableLevelsEnum.equipmentPart}
            entityId={equipmentPart.id}
            entityExternalId={equipmentPart.externalId}
          />

          <CompleteButton onClick={() => navigate(-1)} />
        </ScreenObservationLayout>
      </div>
    </ObservationDraftScope>
  );
};
